import { Controller } from "@hotwired/stimulus"

interface BGStar { x: number; y: number; r: number; a: number }
interface ArmDot  { θ: number; r: number; sc: number; sz: number; op: number; hue: number }
interface Blob    { a: number; d: number; radius: number; rgb: string; op: number }

export default class GalaxyController extends Controller {
  private canvas!: HTMLCanvasElement
  private ctx!: CanvasRenderingContext2D
  private angle = 0
  private curSpeed = 0.00055
  private tgtSpeed = 0.00055
  private readonly BASE  = 0.00055
  private readonly BOOST = 0.0120
  private raf = 0
  private boostTimer = 0
  private ro!: ResizeObserver
  private bgStars: BGStar[] = []
  private armDots: ArmDot[] = []
  private blobs: Blob[] = []

  connect() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    this.canvas = document.createElement('canvas')
    Object.assign(this.canvas.style, {
      position: 'absolute', inset: '0', width: '100%', height: '100%',
      pointerEvents: 'none', zIndex: '0',
    })
    this.element.prepend(this.canvas)
    this.ctx = this.canvas.getContext('2d')!

    this.ro = new ResizeObserver(this.onResize)
    this.ro.observe(this.element)
    this.onResize()

    this.raf = requestAnimationFrame(this.tick)
  }

  disconnect() {
    cancelAnimationFrame(this.raf)
    this.ro?.disconnect()
    this.canvas?.remove()
    clearTimeout(this.boostTimer)
  }

  boost() {
    this.tgtSpeed = this.BOOST
    clearTimeout(this.boostTimer)
    this.boostTimer = window.setTimeout(() => { this.tgtSpeed = this.BASE }, 380)
  }

  private onResize = () => {
    const el = this.element as HTMLElement
    this.canvas.width  = el.offsetWidth
    this.canvas.height = el.offsetHeight
    this.buildScene()
  }

  private buildScene() {
    const { width: w, height: h } = this.canvas
    const maxR = Math.min(w, h) * 0.46

    this.bgStars = Array.from({ length: 200 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.1 + 0.25,
      a: Math.random() * 0.65 + 0.08,
    }))

    this.armDots = []
    for (let arm = 0; arm < 2; arm++) {
      const base = arm * Math.PI
      for (let i = 0; i < 150; i++) {
        const t  = i / 150
        const θ  = t * Math.PI * 3.8 + base
        const r  = t * maxR
        const sc = (Math.random() - 0.5) * maxR * 0.24
        const fade = t < 0.12 ? t / 0.12 : t > 0.72 ? (1 - t) / 0.28 : 1
        this.armDots.push({
          θ, r, sc,
          sz:  Math.random() * 1.7 + 0.4,
          op:  fade * (0.4 + Math.random() * 0.55),
          hue: 255 + Math.random() * 50 - arm * 20,
        })
      }
    }

    this.blobs = [
      { a: 0,               d: maxR * 0.38, radius: maxR * 0.52, rgb: '109,40,217',  op: 0.11 },
      { a: Math.PI * 0.65,  d: maxR * 0.42, radius: maxR * 0.48, rgb: '99,102,241',  op: 0.09 },
      { a: Math.PI * 1.25,  d: maxR * 0.33, radius: maxR * 0.55, rgb: '139,92,246',  op: 0.10 },
      { a: Math.PI * 1.72,  d: maxR * 0.44, radius: maxR * 0.42, rgb: '192,132,252', op: 0.08 },
      { a: Math.PI * 0.35,  d: maxR * 0.50, radius: maxR * 0.38, rgb: '30,58,138',   op: 0.08 },
    ]
  }

  private tick = () => {
    this.curSpeed += (this.tgtSpeed - this.curSpeed) * 0.055
    this.angle    += this.curSpeed

    const { width: w, height: h } = this.canvas
    const cx = w / 2, cy = h / 2
    const maxR = Math.min(w, h) * 0.46

    this.ctx.clearRect(0, 0, w, h)

    for (const s of this.bgStars) {
      this.ctx.beginPath()
      this.ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2)
      this.ctx.fillStyle = `rgba(255,255,255,${s.a})`
      this.ctx.fill()
    }

    this.ctx.save()
    this.ctx.translate(cx, cy)
    this.ctx.rotate(this.angle)

    for (const b of this.blobs) {
      const bx = Math.cos(b.a) * b.d
      const by = Math.sin(b.a) * b.d
      const g  = this.ctx.createRadialGradient(bx, by, 0, bx, by, b.radius)
      g.addColorStop(0,    `rgba(${b.rgb},${b.op})`)
      g.addColorStop(0.55, `rgba(${b.rgb},${+(b.op * 0.35).toFixed(3)})`)
      g.addColorStop(1,     'rgba(0,0,0,0)')
      this.ctx.beginPath()
      this.ctx.arc(bx, by, b.radius, 0, Math.PI * 2)
      this.ctx.fillStyle = g
      this.ctx.fill()
    }

    for (const d of this.armDots) {
      const perp = d.θ + Math.PI / 2
      const x = Math.cos(d.θ) * d.r + Math.cos(perp) * d.sc
      const y = Math.sin(d.θ) * d.r + Math.sin(perp) * d.sc
      this.ctx.beginPath()
      this.ctx.arc(x, y, d.sz, 0, Math.PI * 2)
      this.ctx.fillStyle = `hsla(${d.hue},65%,75%,${d.op.toFixed(3)})`
      this.ctx.fill()
    }

    const core = this.ctx.createRadialGradient(0, 0, 0, 0, 0, maxR * 0.16)
    core.addColorStop(0,   'rgba(255,245,210,0.50)')
    core.addColorStop(0.3, 'rgba(212,175,55,0.18)')
    core.addColorStop(0.7, 'rgba(139,92,246,0.08)')
    core.addColorStop(1,   'rgba(0,0,0,0)')
    this.ctx.beginPath()
    this.ctx.arc(0, 0, maxR * 0.16, 0, Math.PI * 2)
    this.ctx.fillStyle = core
    this.ctx.fill()

    this.ctx.restore()

    this.raf = requestAnimationFrame(this.tick)
  }
}
